import { FC } from 'react'
import { Trash2 } from 'lucide-react'

import FormGroup, { IFormGroupProps } from './FormGroup'
import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui'
import { Icon } from '@/common/components/Icon'

type Props = {
  control: JSX.Element
  isUploading: boolean
  url?: string
  onRemove?: () => void
  label?: string
  previewLabel?: string
  imageStyle?: React.CSSProperties
  width?: number
  height?: number
  imageName?: string
  direction?: 'row' | 'column'
  disabled?: boolean
  formGroupProps?: Omit<IFormGroupProps, 'children'>
}

export const FormUploadPreview: FC<Props> = ({
  control,
  isUploading = false,
  url,
  onRemove,
  previewLabel,
  label,
  imageStyle,
  width,
  height,
  imageName,
  disabled,
  direction,
  formGroupProps,
}) => {
  if (url) {
    return (
      <>
        <FormGroup
          label={previewLabel}
          direction={direction}
          {...formGroupProps}
        >
          <div
            className={cn(
              'w-full rounded-[16px] border border-monochrome-120',
              disabled ? 'bg-monochrome-4/60' : 'bg-monochrome-3-60',
            )}
          >
            <div className="flex items-center text-monochrome-1 justify-between gap-3 p-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <img
                      src={url}
                      className="border-monochrome-120 border hover:border-accent-1 rounded-[10px]"
                      style={{
                        width: width ? `${width}px` : '48px',
                        height: height ? `${height}px` : '48px',
                        objectFit: 'cover',
                        ...imageStyle,
                      }}
                      alt=""
                    />
                  </TooltipTrigger>
                  <TooltipContent className="bg-monochrome-3-60-solid bg-gradient-6 p-2 border-none rounded-2xl">
                    <img
                      src={url}
                      alt={imageName}
                      className="w-full h-full object-cover max-w-2xl rounded-2xl"
                    />
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {isUploading ? (
                <span
                  style={{
                    width: '100%',
                    color: 'var(--monochrome-2)',
                  }}
                >
                  UPLOADING ...
                </span>
              ) : (
                <div className="w-full truncate">
                  <span
                    style={{
                      width: '100%',
                      color: 'var(--monochrome-2)',
                    }}
                  >
                    {imageName}
                  </span>
                </div>
              )}
              <button
                type="button"
                className={cn(
                  'flex h-9 w-9 shrink-0 text-monochrome-1 items-center justify-center rounded-full',
                  disabled
                    ? 'cursor-not-allowed opacity-50'
                    : 'hover:bg-white/5',
                )}
                onClick={disabled ? undefined : onRemove}
                aria-label="Remove file"
              >
                <Icon
                  icon="close-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </button>
            </div>
          </div>
        </FormGroup>
      </>
    )
  } else {
    return (
      <FormGroup label={label} direction={direction} {...formGroupProps}>
        {control}
      </FormGroup>
    )
  }
}
